/**
 * ClientPortalContracts — onglet "Mes contrats" pour le portail client.
 *
 * - Liste des contrats du client (statuts sent/signed)
 * - Modale de signature (canvas + typed name fallback)
 * - Téléchargement PDF signé
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FileSignature, Download, CheckCircle2, Clock, PenTool, Loader2, X, Eye, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from './SignaturePad';
import VipOnboardingCelebration from './VipOnboardingCelebration';

const API = process.env.REACT_APP_BACKEND_URL || '';

function StatusBadge({ status }) {
  if (status === 'signed') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-xs font-medium">
        <CheckCircle2 size={12} /> Signé
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-xs font-medium">
      <Clock size={12} /> À signer
    </span>
  );
}

function SignModal({ contract, onClose, onSigned }) {
  const padRef = useRef(null);
  const [signaturePresent, setSignaturePresent] = useState(false);
  const [city, setCity] = useState('Plobsheim');
  const [ackTos, setAckTos] = useState(false);
  const [signing, setSigning] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // === SEPA — état RIB éditable côté client ===
  const isSepa = contract?.template_id === 'sepa_mandate';
  const [sepa, setSepa] = useState({
    sepa_titulaire: contract?.sepa_prefill?.sepa_titulaire || '',
    sepa_iban: contract?.sepa_prefill?.sepa_iban || '',
    sepa_bic: contract?.sepa_prefill?.sepa_bic || '',
    sepa_nom_banque: contract?.sepa_prefill?.sepa_nom_banque || '',
    sepa_type: contract?.sepa_prefill?.sepa_type || 'RCUR',
  });
  const [ribValid, setRibValid] = useState({ iban: null, bic: null });

  // Validation live IBAN + BIC (uniquement SEPA)
  useEffect(() => {
    if (!isSepa) return;
    const iban = sepa.sepa_iban.trim();
    const bic = sepa.sepa_bic.trim();
    if (!iban && !bic) { setRibValid({ iban: null, bic: null }); return; }
    const t = setTimeout(async () => {
      try {
        const token = localStorage.getItem('client_portal_token');
        const r = await fetch(`${API}/api/client-portal/sepa-validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ iban, bic }),
        });
        if (!r.ok) {
          // Feedback si la validation live échoue (au lieu de silence)
          setRibValid({
            iban: iban ? { ok: false, msg: 'validation indisponible — vérifiez votre connexion' } : null,
            bic: bic ? { ok: false, msg: 'validation indisponible' } : null,
          });
          return;
        }
        const data = await r.json();
        setRibValid({
          iban: iban ? (data.errors?.iban ? { ok: false, msg: data.errors.iban } : { ok: true, pretty: data.iban_pretty }) : null,
          bic: bic ? (data.errors?.bic ? { ok: false, msg: data.errors.bic } : { ok: true }) : null,
        });
      } catch (_) {
        setRibValid({
          iban: iban ? { ok: false, msg: 'validation indisponible — vérifiez votre connexion' } : null,
          bic: bic ? { ok: false, msg: 'validation indisponible' } : null,
        });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [sepa.sepa_iban, sepa.sepa_bic, isSepa]);

  // Validation à la demande (utilisée avant handleSign si ribValid est encore null à cause du debounce)
  const validateRibNow = async () => {
    const iban = sepa.sepa_iban.trim();
    const bic = sepa.sepa_bic.trim();
    if (!iban || !bic) return { iban: null, bic: null };
    try {
      const token = localStorage.getItem('client_portal_token');
      const r = await fetch(`${API}/api/client-portal/sepa-validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ iban, bic }),
      });
      if (!r.ok) return { iban: null, bic: null, error: true };
      const data = await r.json();
      const result = {
        iban: data.errors?.iban ? { ok: false, msg: data.errors.iban } : { ok: true, pretty: data.iban_pretty },
        bic: data.errors?.bic ? { ok: false, msg: data.errors.bic } : { ok: true },
      };
      setRibValid(result);
      return result;
    } catch (_) {
      return { iban: null, bic: null, error: true };
    }
  };

  const sepaMissing = isSepa && (
    !sepa.sepa_titulaire.trim() ||
    !sepa.sepa_iban.trim() ||
    !sepa.sepa_bic.trim()
  );
  const sepaInvalid = isSepa && (
    ribValid.iban?.ok === false ||
    ribValid.bic?.ok === false
  );

  const doPreview = () => {
    const token = localStorage.getItem('client_portal_token');
    // Nouvelle fenêtre avec le PDF non signé
    window.open(
      `${API}/api/client-portal/contracts/${contract.id}/download?token=${encodeURIComponent(token)}&variant=unsigned`,
      '_blank'
    );
  };

  const handleSign = async () => {
    const dataUrl = padRef.current?.getDataURL();
    if (!dataUrl) {
      toast.error('Merci de signer avant de valider');
      return;
    }
    if (!ackTos) {
      toast.error("Merci d'accepter le contrat avant de signer");
      return;
    }
    if (isSepa) {
      if (!sepa.sepa_titulaire.trim()) { toast.error('Titulaire du compte requis'); return; }
      if (!sepa.sepa_iban.trim()) { toast.error('IBAN requis'); return; }
      if (!sepa.sepa_bic.trim()) { toast.error('BIC requis'); return; }
      // Si le debounce n'a pas encore validé (clic rapide), on force la validation ici
      let currentValid = ribValid;
      if (ribValid.iban?.ok !== true || ribValid.bic?.ok !== true) {
        currentValid = await validateRibNow();
      }
      if (currentValid.iban?.ok !== true) {
        toast.error('IBAN invalide : ' + (currentValid.iban?.msg || 'vérifiez votre saisie'));
        return;
      }
      if (currentValid.bic?.ok !== true) {
        toast.error('BIC invalide : ' + (currentValid.bic?.msg || 'vérifiez votre saisie'));
        return;
      }
    }
    setSigning(true);
    try {
      const token = localStorage.getItem('client_portal_token');
      const r = await fetch(`${API}/api/client-portal/contracts/${contract.id}/sign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          signature_data_url: dataUrl,
          signed_at_city: city || 'Plobsheim',
          ...(isSepa ? sepa : {}),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || `HTTP ${r.status}`);
      toast.success('Contrat signé — merci !');
      onSigned?.(data);
      onClose();
    } catch (e) {
      toast.error('Erreur : ' + (e.message || 'inconnue'));
    } finally {
      setSigning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      data-testid="sign-contract-modal-overlay"
    >
      <div
        className="bg-white rounded-t-2xl sm:rounded-xl shadow-2xl w-full sm:max-w-2xl h-[100dvh] sm:h-auto sm:max-h-[92dvh] overflow-hidden flex flex-col"
        onMouseDown={(e) => e.stopPropagation()}
        data-testid="sign-contract-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-[#d4a850]/40 bg-gradient-to-r from-[#0d1a12] to-[#1e3a2c] text-white flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <PenTool size={18} className="text-[#d4a850] flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-serif text-base sm:text-lg truncate" style={{ fontFamily: 'Playfair Display, serif' }}>
                Signature électronique
              </div>
              <div className="text-xs text-white/70 italic truncate">{contract.template_label}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white flex-shrink-0" data-testid="close-sign-modal">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5 flex-1 min-h-0">
          <div>
            <button
              onClick={doPreview}
              className="flex items-center gap-1.5 text-sm text-[#0d1a12] hover:text-[#b78a1e]"
              data-testid="preview-before-sign-btn"
            >
              <Eye size={14} /> Lire le contrat en entier
            </button>
          </div>

          {/* SEPA — Complétez votre RIB */}
          {isSepa && (
            <div className="rounded-lg border-2 border-[#d4a850]/60 bg-[#fdfcf6] p-3 sm:p-4 space-y-3" data-testid="sepa-rib-form">
              <div className="text-xs uppercase tracking-wider text-[#8a6d1e] font-medium">
                🏦 Vos coordonnées bancaires (RIB)
              </div>
              <div>
                <label className="text-[11px] text-neutral-600 block mb-1">Titulaire du compte *</label>
                <input
                  type="text"
                  value={sepa.sepa_titulaire}
                  onChange={(e) => setSepa((s) => ({ ...s, sepa_titulaire: e.target.value }))}
                  placeholder="Ex : Marie Dupont"
                  className="w-full rounded border border-neutral-300 px-3 py-2 text-sm focus:border-[#d4a850] focus:outline-none"
                  data-testid="portal-sepa-titulaire"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3">
                <div className="sm:col-span-2">
                  <label className="text-[11px] text-neutral-600 mb-1 flex items-center gap-1.5">
                    IBAN *
                    {ribValid.iban?.ok === true && <CheckCircle2 size={12} className="text-emerald-600" />}
                    {ribValid.iban?.ok === false && <span className="text-[10px] text-red-600 italic">{ribValid.iban.msg}</span>}
                  </label>
                  <input
                    type="text"
                    value={sepa.sepa_iban}
                    onChange={(e) => setSepa((s) => ({ ...s, sepa_iban: e.target.value.toUpperCase() }))}
                    placeholder="FR76 3000 4000 ..."
                    className={`w-full rounded border px-3 py-2 text-sm font-mono focus:outline-none ${
                      ribValid.iban?.ok === true ? 'border-emerald-400 bg-emerald-50/40 focus:border-emerald-500'
                      : ribValid.iban?.ok === false ? 'border-red-400 bg-red-50/40 focus:border-red-500'
                      : 'border-neutral-300 focus:border-[#d4a850]'
                    }`}
                    data-testid="portal-sepa-iban"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-neutral-600 mb-1 flex items-center gap-1.5">
                    BIC *
                    {ribValid.bic?.ok === true && <CheckCircle2 size={12} className="text-emerald-600" />}
                    {ribValid.bic?.ok === false && <span className="text-[10px] text-red-600 italic">{ribValid.bic.msg}</span>}
                  </label>
                  <input
                    type="text"
                    value={sepa.sepa_bic}
                    onChange={(e) => setSepa((s) => ({ ...s, sepa_bic: e.target.value.toUpperCase() }))}
                    placeholder="BNPAFRPPXXX"
                    className={`w-full rounded border px-3 py-2 text-sm font-mono focus:outline-none ${
                      ribValid.bic?.ok === true ? 'border-emerald-400 bg-emerald-50/40 focus:border-emerald-500'
                      : ribValid.bic?.ok === false ? 'border-red-400 bg-red-50/40 focus:border-red-500'
                      : 'border-neutral-300 focus:border-[#d4a850]'
                    }`}
                    data-testid="portal-sepa-bic"
                  />
                </div>
              </div>
              <div>
                <label className="text-[11px] text-neutral-600 block mb-1">Nom de votre banque</label>
                <input
                  type="text"
                  value={sepa.sepa_nom_banque}
                  onChange={(e) => setSepa((s) => ({ ...s, sepa_nom_banque: e.target.value }))}
                  placeholder="Ex : BNP Paribas"
                  className="w-full rounded border border-neutral-300 px-3 py-2 text-sm focus:border-[#d4a850] focus:outline-none"
                  data-testid="portal-sepa-nom-banque"
                />
              </div>
              <div className="text-[10px] text-neutral-500 italic border-t border-[#d4a850]/30 pt-2">
                En signant, vous autorisez le prélèvement de vos paiements sur ce compte. Vous pouvez révoquer ce mandat à tout moment (droit de rétractation SEPA).
              </div>
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-wider text-[#8a7a56] mb-2 font-medium">
              Votre signature
            </label>
            <SignaturePad
              ref={padRef}
              height={140}
              onChange={(url) => setSignaturePresent(!!url)}
            />
            <div className="text-[11px] text-neutral-500 mt-1 italic">
              Utilisez votre souris ou votre doigt sur écran tactile.
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#8a7a56] mb-1">
                Fait à
              </label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full rounded border border-neutral-300 px-2.5 py-1.5 text-sm focus:border-[#d4a850] focus:outline-none"
                data-testid="signed-city-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider text-[#8a7a56] mb-1">Le</label>
              <input
                type="text"
                value={new Date().toLocaleDateString('fr-FR')}
                readOnly
                className="w-full rounded border border-neutral-200 bg-neutral-50 px-2.5 py-1.5 text-sm text-neutral-600"
              />
            </div>
          </div>

          <label className="flex items-start gap-2 cursor-pointer text-sm bg-[#fdfcf6] border border-[#d4a850]/40 rounded p-3">
            <input
              type="checkbox"
              checked={ackTos}
              onChange={(e) => setAckTos(e.target.checked)}
              className="mt-0.5 rounded border-neutral-300"
              data-testid="ack-tos-checkbox"
            />
            <span className="text-neutral-800 leading-snug">
              Je reconnais avoir pris connaissance du contrat et de son règlement intérieur, et je m&apos;engage à
              les respecter. Ma signature électronique a la même valeur juridique qu&apos;une signature manuscrite.
            </span>
          </label>

          <div className="text-[10px] text-neutral-500 italic border-t border-neutral-100 pt-3">
            Votre adresse IP et l&apos;horodatage précis (UTC) seront enregistrés dans le PDF signé pour garantir la
            validité juridique de votre engagement.
          </div>
        </div>

        {/* Footer — sticky avec padding safe-area pour iOS */}
        <div
          className="px-4 sm:px-6 py-3 sm:py-4 border-t border-neutral-200 bg-neutral-50 flex flex-col gap-2 flex-shrink-0"
          style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
          {/* Message d'aide permanent quand SEPA + RIB manquant/invalide */}
          {isSepa && (sepaMissing || sepaInvalid) && (
            <div
              className="text-[11px] sm:text-xs rounded border border-amber-300 bg-amber-50 text-amber-900 px-2.5 py-1.5 flex items-center gap-1.5"
              data-testid="sepa-ready-warning"
            >
              <AlertCircle size={13} className="flex-shrink-0" />
              {sepaMissing
                ? 'Complétez votre RIB (Titulaire + IBAN + BIC) avant de signer'
                : `RIB invalide : ${ribValid.iban?.ok === false ? 'IBAN' : ''}${ribValid.iban?.ok === false && ribValid.bic?.ok === false ? ' + ' : ''}${ribValid.bic?.ok === false ? 'BIC' : ''} — corrigez pour continuer`}
            </div>
          )}
          <div className="flex items-center justify-between gap-2">
            <button
              onClick={onClose}
              className="px-3 sm:px-4 py-2 rounded border border-neutral-300 text-sm text-neutral-700 hover:bg-neutral-100"
              data-testid="cancel-sign-btn"
            >
              Annuler
            </button>
            <button
              onClick={handleSign}
              disabled={!signaturePresent || !ackTos || signing || (isSepa && (sepaMissing || sepaInvalid))}
              className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 rounded bg-[#0d1a12] text-white text-sm hover:bg-[#1e3a2c] disabled:opacity-40 disabled:cursor-not-allowed shadow-sm"
              data-testid="submit-sign-btn"
            >
              {signing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              <span className="hidden xs:inline">Signer et envoyer</span>
              <span className="xs:hidden">Signer</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ClientPortalContracts({ onCountChange, highlight = false, refreshKey = 0 }) {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [signingContract, setSigningContract] = useState(null);
  const [celebrateContract, setCelebrateContract] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('client_portal_token');
      const r = await fetch(`${API}/api/client-portal/contracts`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const list = data.contracts || [];
      setContracts(list);
      // Remonte au parent le nombre de contrats en attente
      if (onCountChange) {
        const pending = list.filter((c) => c.status !== 'signed').length;
        onCountChange(pending);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [onCountChange]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleDownload = (c) => {
    const token = localStorage.getItem('client_portal_token');
    window.open(
      `${API}/api/client-portal/contracts/${c.id}/download?token=${encodeURIComponent(token)}`,
      '_blank'
    );
  };

  const pending = contracts.filter((c) => c.status !== 'signed');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6 text-neutral-500">
        <Loader2 size={20} className="animate-spin mr-2" /> Chargement de vos contrats…
      </div>
    );
  }

  // Cas particulier : aucun contrat en attente MAIS célébration VIP à afficher
  // (le client vient de signer son dernier contrat → il n'y a plus rien à lister
  // mais le popup doit s'afficher). Sans ce fragment, le return null ci-dessous
  // court-circuiterait le VipOnboardingCelebration.
  if (pending.length === 0) {
    if (celebrateContract) {
      return (
        <VipOnboardingCelebration
          contract={celebrateContract}
          onClose={() => setCelebrateContract(null)}
        />
      );
    }
    return null;
  }

  return (
    <div
      className={`space-y-3 ${highlight ? 'contracts-highlight' : ''}`}
      data-testid="client-portal-contracts"
    >
      <div className="space-y-2">
        {pending.map((c) => (
          <div
            key={c.id}
            className="bg-white rounded-lg border-2 border-amber-200 shadow-sm hover:shadow-md transition-shadow p-3"
            data-testid={`pending-contract-${c.id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <FileSignature size={14} className="text-[#b78a1e] flex-shrink-0" />
                  <span className="font-medium text-[#0d1a12] text-sm">{c.template_label}</span>
                  <StatusBadge status={c.status} />
                </div>
                <div className="text-[11px] text-neutral-500 mt-0.5">
                  Envoyé le{' '}
                  {new Date(c.sent_at || c.created_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric',
                  })}
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <button
                  onClick={() => handleDownload(c)}
                  className="flex items-center gap-1 px-2 py-1.5 rounded border border-neutral-300 text-xs text-neutral-700 hover:bg-neutral-100"
                  data-testid={`preview-contract-${c.id}`}
                >
                  <Eye size={11} /> Lire
                </button>
                <button
                  onClick={() => setSigningContract(c)}
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded bg-[#0d1a12] text-white text-xs hover:bg-[#1e3a2c] shadow-sm"
                  data-testid={`sign-contract-${c.id}`}
                >
                  <PenTool size={11} /> Signer
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {signingContract && (
        <SignModal
          contract={signingContract}
          onClose={() => setSigningContract(null)}
          onSigned={() => {
            const signed = signingContract;
            load();
            setCelebrateContract(signed);
          }}
        />
      )}

      {celebrateContract && (
        <VipOnboardingCelebration
          contract={celebrateContract}
          onClose={() => setCelebrateContract(null)}
        />
      )}

      <style>{`
        @keyframes contractsPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);      }
          50%      { box-shadow: 0 0 0 8px rgba(220, 38, 38, 0.18); }
        }
        .contracts-highlight {
          border-radius: 12px;
          animation: contractsPulse 1.6s ease-in-out 3;
        }
      `}</style>
    </div>
  );
}
